import {
  COLOR_PAGES,
  COLOR_PAGE_NOTE,
  COLOR_PAGES_PATH,
  colorPageFor,
  colorPagePath,
  colorSlugFromPath,
  type ColorPage,
} from "@roof-hawk/core";
import { useEffect } from "react";

import { COMPANY_LOGO_URL } from "../../logo";
import { ColorViewer } from "./ColorViewer";
import { ColorChart } from "./PresentedDocumentView";

export { colorSlugFromPath };

/**
 * `/colors` and `/colors/<slug>`: the colour options a rep sends on their own.
 *
 * Jonathon, 2026-09-30: "sometimes we want to send all of these color options
 * to customers via links". Each page shows what the matching card on a
 * proposal opens, from the same data (`COLOR_PAGES` in packages/core), so
 * there is one place to change a colour and the two can never disagree.
 *
 * A stranger's page, like the proposal: no sign-in, nothing private, and
 * nothing read from the database. It is drawn in the proposal's own pinned
 * palette (`.pdoc`), so it looks the same on every phone.
 */
export function ColorsPage({ slug }: { slug: string }) {
  if (slug === "") return <ColorList />;
  const page = colorPageFor(slug);
  if (!page) return <NotFound />;
  return <OnePage page={page} />;
}

/** The page frame: the dark band of the proposal's colour section. */
function Frame({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="pdoc pdoc-colors-page">
      <section className="pdoc-dark">
        <div className="wrap">
          <img className="corner" src={COMPANY_LOGO_URL} alt="" />
          <p className="pdoc-eyebrow">Color Options</p>
          <h2 className="pdoc-h">{heading}</h2>
          {children}
        </div>
      </section>
    </div>
  );
}

/** Every page, grouped, each one card to tap. */
function ColorList() {
  const groups = [...new Set(COLOR_PAGES.map((page) => page.group))];
  return (
    <Frame heading="See Our Color Options">
      {groups.map((group) => (
        <div key={group} className="pdoc-colors-group">
          <h3>{group}</h3>
          <div className="pdoc-color-cards">
            {COLOR_PAGES.filter((page) => page.group === group).map(
              (page, index) => (
                <a
                  key={page.slug}
                  className={`pdoc-color-card tone${index % 4}`}
                  href={colorPagePath(page)}
                >
                  <span className="head">
                    <span className="name">{page.label}</span>
                  </span>
                  <span className="cta">View colors →</span>
                </a>
              ),
            )}
          </div>
        </div>
      ))}
    </Frame>
  );
}

/** One lineup, shown the way its card on a proposal shows it. */
function OnePage({ page }: { page: ColorPage }) {
  const { content } = page;

  /* A chart somebody else publishes, or a PDF of ours: go straight there. */
  useEffect(() => {
    if (content.kind === "link") window.location.replace(content.url);
  }, [content]);

  if (content.kind === "viewer")
    return (
      <ColorViewer
        viewer={content.viewer}
        onClose={() => window.location.assign(COLOR_PAGES_PATH)}
      />
    );

  if (content.kind === "link")
    return (
      <Frame heading={page.label}>
        <p className="pdoc-hub-foot">
          Opening the color chart…{" "}
          <a href={content.url}>Tap here if it does not open.</a>
        </p>
      </Frame>
    );

  return (
    <Frame heading={page.label}>
      <div className="pdoc-chart">
        <ColorChart
          colors={content.colors.map((color) => ({
            ...color,
            photos: [...color.photos],
          }))}
          folder={content.folder}
          what={content.what}
        />
      </div>
      <p className="pdoc-hub-foot">{COLOR_PAGE_NOTE}</p>
      <p className="pdoc-colors-back">
        <a href={COLOR_PAGES_PATH}>← All color options</a>
      </p>
    </Frame>
  );
}

function NotFound() {
  return (
    <Frame heading="This color page isn't here">
      <p className="pdoc-hub-foot">
        The link may be old or mistyped.{" "}
        <a href={COLOR_PAGES_PATH}>See every color option.</a>
      </p>
    </Frame>
  );
}
